import React, { useEffect, useState } from 'react';
import {
  getActiveCurrencyRate,
  createCurrencyRate,
  CurrencyRate,
} from '../lib/currencyRateApi';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import Button from '../components/ui/Button';
import { ErrorState } from '../components/ui/States';

const CURRENCY_CONVERSION_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>You can edit the TT$ price from here.</p>
    <p>Once the updated price is applied, it will be reflected across all modules.</p>
  </div>
);

export const CurrencyConversionPage: React.FC = () => {
  const [activeRate, setActiveRate] = useState<CurrencyRate | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Form state - TTD amount (USD is always 1.00)
  const [ttdAmount, setTtdAmount] = useState<number>(6.0);

  const fetchActiveRate = async () => {
    setLoading(true);
    setError(null);
    try {
      const rate = await getActiveCurrencyRate('USD', 'TTD');
      setActiveRate(rate);
      setTtdAmount(rate.rate ?? 0);
    } catch (e: any) {
      console.error('Failed to fetch active rate', e);
      // Don't show error if no rate exists yet
      if (!e.message?.includes('404')) {
        setError(e.message || 'Failed to load active rate');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      // Rate is simply the TTD amount (since USD is always 1.00)
      const rate = ttdAmount;

      if (rate <= 0 || !isFinite(rate)) {
        setError('Invalid exchange rate');
        setSaving(false);
        return;
      }

      await createCurrencyRate({
        fromCurrency: 'USD',
        toCurrency: 'TTD',
        rate: rate,
        notes: `Rate set: 1 USD = ${rate.toFixed(2)} TTD`,
        effectiveFrom: new Date().toISOString(),
      });

      setSuccessMsg('Exchange rate updated successfully!');
      await fetchActiveRate();
    } catch (e: any) {
      console.error('Failed to save rate', e);
      setError(e.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchActiveRate();
  }, []);

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* The same header band as Settings, whose Exchange Rate tab this screen
          duplicates under its own URL: switching between the two must not move
          the title (docs/DESIGN.md). */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Currency conversion</h1>
            <ModuleInfoButton content={CURRENCY_CONVERSION_INFO_CONTENT} ariaLabel="Currency Conversion information" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto flex items-start justify-center p-4 sm:p-6">
      {/* Simple Currency Conversion Box */}
      <div className="w-full max-w-[712px] bg-white border border-slate-200 rounded-lg p-6 sm:p-8 md:p-12 flex flex-col">

        {/* Alerts. "Try again" reloads the active rate, which is the recovery
            for a failed load and a sane re-read after a failed save. */}
        {(error || successMsg) && (
          <div className="mb-6">
            {error && (
              <div className="mb-3">
                <ErrorState message={error} onRetry={fetchActiveRate} />
              </div>
            )}
            {successMsg && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3">
                {successMsg}
              </div>
            )}
          </div>
        )}

        {/* Currency Inputs */}
        <div className="flex items-center justify-center gap-6 mb-8">
          {/* TTD Input. The two flags stay hand-drawn SVG: they are small
              illustrations, not interface icons, and lucide has no flag set.
              The chevron that used to sit beside each one is gone — neither
              currency was ever selectable, so it advertised a dropdown that
              does not exist. */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-6 rounded overflow-hidden border border-slate-300">
                <svg viewBox="0 0 5 3" className="w-full h-full" aria-hidden="true">
                  <rect width="5" height="3" fill="#CE1126"/>
                  <path d="M0,0 L5,3 M0,3 L5,0" stroke="#000000" strokeWidth="0.6"/>
                  <path d="M0,0 L5,3 M0,3 L5,0" stroke="#FFFFFF" strokeWidth="0.4"/>
                </svg>
              </div>
              <span className="text-lg font-semibold text-slate-800">TTD</span>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={ttdAmount}
              onChange={(e) => setTtdAmount(parseFloat(e.target.value) || 0)}
              disabled={loading || saving}
              aria-label="Trinidad and Tobago dollars per 1 US dollar"
              className="w-32 px-4 py-2 text-center text-lg font-medium border border-slate-300 rounded-md focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-60 disabled:bg-slate-100"
              placeholder="6.00"
            />
          </div>

          {/* USD Input */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-6 rounded overflow-hidden border border-slate-300">
                <svg viewBox="0 0 19 10" className="w-full h-full" aria-hidden="true">
                  <rect width="19" height="10" fill="#B22234"/>
                  <path d="M0,0 h19 M0,1 h19 M0,2 h19 M0,3 h19 M0,4 h19 M0,5 h19 M0,6 h19 M0,7 h19 M0,8 h19 M0,9 h19" stroke="#FFFFFF" strokeWidth="0.7"/>
                  <rect width="7.6" height="5.4" fill="#3C3B6E"/>
                </svg>
              </div>
              <span className="text-lg font-semibold text-slate-800">USD</span>
            </div>
            <input
              type="text"
              value="1.00"
              readOnly
              disabled
              aria-label="US dollars"
              className="w-32 px-4 py-2 text-center text-lg font-medium border border-slate-300 rounded-md bg-slate-100 text-slate-600 cursor-not-allowed"
            />
          </div>
        </div>

        {/* Apply Button */}
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={loading || ttdAmount <= 0}
          loading={saving}
          className="w-full"
        >
          {saving ? 'Applying...' : 'Apply'}
        </Button>

        {/* Current Rate Display */}
        {activeRate && !loading && (
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-600">
              {activeRate.rate != null ? (
                <>Current rate: <span className="font-semibold text-slate-800 tabular-nums">1 USD = {activeRate.rate.toFixed(2)} TTD</span></>
              ) : (
                <>Not set yet. Every TT$/US$ conversion is refused until a rate is saved.</>
              )}
            </p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
